import { create } from 'zustand';
import { messagesApi } from '../api/messages.api.js';

export const useMessageStore = create((set, get) => ({
  // messages keyed by contactId: { [contactId]: Message[] }
  messages: {},
  isLoading: false,

  fetchMessages: async (contactId) => {
    set({ isLoading: true });
    try {
      const { data } = await messagesApi.getByContact(contactId, { limit: 100 });
      set((state) => ({
        messages: { ...state.messages, [contactId]: data.data },
      }));
    } finally {
      set({ isLoading: false });
    }
  },

  addMessage: (contactId, message) => {
    set((state) => {
      const existing = state.messages[contactId] || [];
      // Avoid duplicates
      if (existing.find((m) => m.id === message.id)) return state;
      return {
        messages: {
          ...state.messages,
          [contactId]: [...existing, message],
        },
      };
    });
  },

  replaceMessage: (contactId, tempId, message) => {
    set((state) => {
      const existing = state.messages[contactId] || [];
      const withoutTemp = existing.filter((m) => m.id !== tempId);
      const alreadyExists = withoutTemp.some((m) => m.id === message.id);
      return {
        messages: {
          ...state.messages,
          [contactId]: alreadyExists ? withoutTemp : [...withoutTemp, message],
        },
      };
    });
  },

  markMessageFailed: (contactId, messageId, errorMessage) => {
    set((state) => {
      const existing = state.messages[contactId] || [];
      return {
        messages: {
          ...state.messages,
          [contactId]: existing.map((m) => (
            m.id === messageId ? { ...m, status: 'FAILED', errorMessage } : m
          )),
        },
      };
    });
  },

  updateMessageStatus: (contactId, twilioSid, status, errorMessage) => {
    set((state) => {
      const msgs = state.messages[contactId] || [];
      return {
        messages: {
          ...state.messages,
          [contactId]: msgs.map((m) =>
            m.twilioSid === twilioSid
              ? { ...m, status, ...(errorMessage !== undefined && { errorMessage }) }
              : m
          ),
        },
      };
    });
  },

  // Optimistic: add message immediately, update when confirmed
  optimisticAdd: (contactId, message) => {
    set((state) => ({
      messages: {
        ...state.messages,
        [contactId]: [...(state.messages[contactId] || []), message],
      },
    }));
  },

  getMessages: (contactId) => get().messages[contactId] || [],
}));
